import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistUser(user) {
  if (user) localStorage.setItem('user', JSON.stringify(user));
  else localStorage.removeItem('user');
}

// JWT 토큰에서 만료 시간 추출
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // ms 변환
  } catch {
    return null;
  }
}

// 토큰 만료까지 남은 시간(ms)
function getTimeUntilExpiry(token) {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;
  return expiry - Date.now();
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getStoredUser());
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  // 토큰 갱신 함수
  const refreshToken = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;

    try {
      const res = await api.post('/auth/refresh', {}, { skipAuthRedirect: true });
      if (res.data.success && res.data.token) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
        scheduleTokenRefresh(res.data.token);
      }
    } catch {
      // 갱신 실패 시 api interceptor에서 처리
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 토큰 만료 전 자동 갱신 스케줄링
  const scheduleTokenRefresh = useCallback((tok) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!tok) return;

    const timeUntilExpiry = getTimeUntilExpiry(tok);
    if (timeUntilExpiry <= 0) return;

    // 만료 30분 전에 갱신 (최소 10초 후)
    const refreshIn = Math.max(timeUntilExpiry - 30 * 60 * 1000, 10000);

    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, refreshIn);
  }, [refreshToken]);

  const checkAuth = useCallback(async () => {
    const t = localStorage.getItem('token');
    if (!t) { persistUser(null); setUser(null); setToken(null); setLoading(false); return; }

    // 토큰 만료 확인
    const timeLeft = getTimeUntilExpiry(t);
    if (timeLeft <= 0) {
      localStorage.removeItem('token');
      persistUser(null);
      setUser(null); setToken(null); setLoading(false);
      return;
    }

    try {
      const res = await api.get('/auth/me', { skipAuthRedirect: true });
      setUser(res.data.user);
      persistUser(res.data.user);
      setToken(t);
      scheduleTokenRefresh(t);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        persistUser(null);
        setUser(null); setToken(null);
      } else {
        const cachedUser = getStoredUser();
        if (cachedUser) setUser(cachedUser);
        setToken(t);
        scheduleTokenRefresh(t);
      }
    } finally { setLoading(false); }
  }, [scheduleTokenRefresh]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // 다른 탭에서 로그아웃/로그인 감지
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          persistUser(null);
          setUser(null); setToken(null);
        } else {
          checkAuth();
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuth]);

  // 타이머 정리
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { token: t, user: u } = res.data;
      localStorage.setItem('token', t);
      persistUser(u);
      setToken(t); setUser(u);
      scheduleTokenRefresh(t);
      return { success: true, user: u };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || '로그인에 실패했습니다.' };
    }
  };

  const register = async (data) => {
    try {
      const res = await api.post('/auth/register', data);
      const { token: t, user: u } = res.data;
      localStorage.setItem('token', t);
      persistUser(u);
      setToken(t); setUser(u);
      scheduleTokenRefresh(t);
      return { success: true, user: u };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || '가입에 실패했습니다.' };
    }
  };

  const logout = () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // 서버에 토큰 무효화 요청 (토큰 제거 전에 호출해야 Authorization 헤더가 실린다). 실패해도 로컬 로그아웃은 진행.
    try { api.post('/auth/logout').catch(() => {}); } catch (e) {}
    localStorage.removeItem('token');
    persistUser(null);
    setToken(null); setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, register, checkAuth, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};
